import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { App, Skeleton } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { getDashboardSummary } from '../api/dashboard';
import { retryJob } from '../api/job';
import { Topbar } from '../layouts/Topbar';
import { Donut, type DonutDatum } from '../components/Donut';
import { BarChart } from '../components/BarChart';
import { EventTimeline } from '../components/Timeline';
import { TenantStatusBadge, JobStatusBadge } from '../components/StatusBadge';
import { formatDateTime } from '../utils/datetime';

const DIST_ORDER = ['active', 'grace', 'read_only', 'manually_locked', 'suspended', 'provisioning', 'terminated'] as const;
const DIST_COLORS: Record<string, string> = {
  active: '#16A34A', grace: '#D97706', read_only: '#0EA5E9',
  manually_locked: '#7C3AED', suspended: '#DC2626',
  provisioning: '#0369A1', terminated: '#475569',
};
const STATUS_LABEL: Record<string, string> = {
  active: '活跃', grace: '宽限期', read_only: '只读', suspended: '已停服',
  manually_locked: '手动冻结', terminated: '已清退', provisioning: '开通中',
};

function fmtToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// "2026-05-11" -> "5/11"
function shortDay(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${m}/${d}`;
}

export function DashboardPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: getDashboardSummary,
  });

  const retry = useMutation({
    mutationFn: (id: number) => retryJob(id),
    onSuccess: () => {
      message.success('已重置为 pending，job-runner 将自动重试');
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
    onError: (e: unknown) => {
      message.error(e instanceof Error ? e.message : '重试失败');
    },
  });

  return (
    <>
      <Topbar crumbs={[{ label: '运营' }, { label: '仪表盘' }]} />
      <div className="page">
        <div className="page-header">
          <div className="page-header__title">
            <h1>仪表盘</h1>
            <div className="page-header__sub">{fmtToday()} · 全平台租户与任务的实时概览</div>
          </div>
          <div className="page-header__actions">
            <button type="button" className="btn btn-secondary" onClick={() => refetch()} disabled={isFetching}>
              <ReloadOutlined spin={isFetching} /> <span>刷新</span>
            </button>
            <Link to="/tenants/new" className="btn btn-primary">
              <PlusOutlined /> <span>新建租户</span>
            </Link>
          </div>
        </div>

        {isLoading || !data ? <Skeleton active /> : (
          <DashboardBody data={data} onRetry={(id) => retry.mutate(id)} retrying={retry.isPending} />
        )}
      </div>
    </>
  );
}

interface DashboardBodyProps {
  data: Awaited<ReturnType<typeof getDashboardSummary>>;
  onRetry: (id: number) => void;
  retrying: boolean;
}

function DashboardBody({ data, onRetry, retrying }: DashboardBodyProps) {
  const total = Object.values(data.distribution).reduce((a, b) => a + b, 0);
  const graceReadOnly = (data.distribution.grace ?? 0) + (data.distribution.read_only ?? 0);
  const expiring7 = data.expiring.filter((e) => e.daysLeft <= 7).length;

  const barData = data.dailyActivity.map((d) => ({
    label: shortDay(d.day),
    value: d.created + d.renewed,
  }));

  const focusedJobs = data.recentJobs.slice(0, 5);

  return (
    <>
      {/* KPI 行 */}
      <div className="kpi-row">
        <div className="kpi">
          <div className="kpi__label">活跃租户</div>
          <div className="kpi__value">{data.distribution.active ?? 0}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">宽限期 / 只读</div>
          <div className="kpi__value">{graceReadOnly}</div>
          <div className="kpi__delta flat">需关注</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">即将到期 (7 天内)</div>
          <div className="kpi__value">{expiring7}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">24h 失败任务</div>
          <div className="kpi__value">{data.failed24h}</div>
          <div className={`kpi__delta ${data.failed24h > 0 ? 'down' : 'flat'}`}>
            <Link to="/jobs" style={{ color: 'inherit' }}>点击查看</Link>
          </div>
        </div>
      </div>

      {/* 分布 + Bar chart */}
      <div className="grid-2">
        <div className="card">
          <div className="card__header">
            <div className="card__title">租户状态分布</div>
            <span className="text-xs text-muted">共 <strong>{total}</strong> 个租户</span>
          </div>
          <div className="card__body">
            <div className="donut-row">
              <Donut
                total={total}
                data={DIST_ORDER.map<DonutDatum>((k) => ({
                  key: k,
                  label: STATUS_LABEL[k] ?? k,
                  value: data.distribution[k] ?? 0,
                  color: DIST_COLORS[k],
                }))}
              />
              <div className="legend">
                {DIST_ORDER.filter((k) => (data.distribution[k] ?? 0) > 0).map((k) => (
                  <div key={k} className="legend__item">
                    <span className="legend__chip" style={{ background: DIST_COLORS[k] }} />
                    <span className="legend__name">{STATUS_LABEL[k]}</span>
                    <span className="legend__count">{data.distribution[k]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__header">
            <div className="card__title">近 7 天租户活跃</div>
            <span className="text-xs text-muted">每日新建 + 续签</span>
          </div>
          <div className="card__body">
            <BarChart data={barData} />
          </div>
        </div>
      </div>

      {/* 即将到期 + 事件 */}
      <div className="grid-3-2">
        <div className="card">
          <div className="card__header">
            <div className="card__title">即将到期（7 天内 / 已进入宽限期）</div>
            <Link to="/tenants" className="text-xs">查看全部 →</Link>
          </div>
          <div className="card__body" style={{ padding: 0 }}>
            <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr><th>租户</th><th>套餐</th><th>状态</th><th>到期时间</th><th>距到期</th><th className="actions"></th></tr>
                </thead>
                <tbody>
                  {data.expiring.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-fg-3)', padding: 24 }}>暂无即将到期租户</td></tr>
                  )}
                  {data.expiring.map((e) => {
                    const overdue = e.daysLeft < 0;
                    const warn = !overdue && e.daysLeft <= 7;
                    const fillCls = overdue ? 'progress__fill--danger' : warn ? 'progress__fill--warning' : '';
                    const text = overdue ? `已逾期 ${-e.daysLeft} 天` : `${e.daysLeft} 天`;
                    const textColor = overdue ? 'var(--c-destructive)' : warn ? 'var(--c-warning)' : 'var(--c-fg)';
                    const width = Math.max(8, Math.min(100, 100 - Math.abs(e.daysLeft) * 4));
                    return (
                      <tr key={e.tenant.id}>
                        <td>
                          <div className="text-strong">{e.tenant.display_name}</div>
                          <div className="text-xs text-muted text-mono">
                            <a
                              href={`https://${e.tenant.code}.owlsome.kzdatong.com`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(ev) => ev.stopPropagation()}
                            >{e.tenant.code}.owlsome.kzdatong.com</a>
                          </div>
                        </td>
                        <td><span className="tag">{e.contract.plan_code}</span></td>
                        <td><TenantStatusBadge status={e.tenant.status} /></td>
                        <td className="text-mono num">{e.contract.end_at.slice(0, 10)}</td>
                        <td className="num">
                          <div className="text-strong" style={{ marginBottom: 4, color: textColor }}>{text}</div>
                          <div className="progress" style={{ width: 100 }}>
                            <div className={`progress__fill ${fillCls}`} style={{ width: `${width}%` }} />
                          </div>
                        </td>
                        <td className="actions">
                          <Link to={`/tenants/${e.tenant.id}`} className="btn btn-ghost btn-sm">详情</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__header">
            <div className="card__title">近期生命周期事件</div>
            <Link to="/tenants" className="text-xs">查看全部 →</Link>
          </div>
          <div className="card__body">
            {data.recentEvents.length === 0 ? (
              <div style={{ color: 'var(--c-fg-3)', textAlign: 'center', padding: 24 }}>暂无事件</div>
            ) : (
              <EventTimeline events={data.recentEvents.slice(0, 5)} />
            )}
          </div>
        </div>
      </div>

      {/* 失败 / 运行中任务 */}
      <div className="card mt-6">
        <div className="card__header">
          <div className="card__title">最近失败 / 长时间运行的任务</div>
          <Link to="/jobs" className="text-xs">前往任务监控 →</Link>
        </div>
        <div className="card__body" style={{ padding: 0 }}>
          <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Job ID</th><th>租户</th><th>类型</th><th>状态</th>
                  <th>调度</th><th>尝试</th><th>错误信息</th><th className="actions">操作</th>
                </tr>
              </thead>
              <tbody>
                {focusedJobs.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--c-fg-3)', padding: 24 }}>暂无</td></tr>
                )}
                {focusedJobs.map((j) => (
                  <tr key={j.id}>
                    <td className="text-mono">#{j.id}</td>
                    <td className="text-mono">
                      {j.tenant_code
                        ? j.tenant_code
                        : <span className="tag" style={{ fontFamily: 'var(--font-sans)' }}>平台级</span>}
                    </td>
                    <td><span className="tag">{j.job_type}</span></td>
                    <td><JobStatusBadge status={j.status} /></td>
                    <td className="text-mono num" title={j.scheduled_at}>{formatDateTime(j.scheduled_at)}</td>
                    <td className="num">{j.attempts}</td>
                    <td className="text-xs" style={{ maxWidth: 280, color: 'var(--c-fg-3)' }}>{j.last_error ?? '—'}</td>
                    <td className="actions">
                      {j.status === 'failed' ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => onRetry(j.id)}
                          disabled={retrying}
                        >重试</button>
                      ) : (
                        <Link to="/jobs" className="btn btn-ghost btn-sm">查看</Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default DashboardPage;
